import React, { useMemo, useCallback, useEffect, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import client from '@atlassian/jira-apollo-gira/src/index.tsx';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import { useSidebar } from '@atlassian/jira-business-app-wrapper/src/controllers/sidebar/index.tsx';
import type { SortingDirection } from '@atlassian/jira-business-common/src/common/types/sorting.tsx';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants/src/index.tsx';
import type { Filters as FiltersType } from '@atlassian/jira-business-filters/src/common/types.tsx';
import { useSearchText } from '@atlassian/jira-business-filters/src/controllers/simple-search/index.tsx';
import { serialiseJql } from '@atlassian/jira-business-filters/src/controllers/utils.tsx';
import { useIssueEvents } from '@atlassian/jira-business-issue-events-observable/src/index.tsx';
import { useScrollToEnd } from '@atlassian/jira-business-scroll-to-end/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useQueryParam } from '@atlassian/react-resource-router';
import { useUnscheduledItems as useUnscheduledItemsService } from '../../services/unscheduled-items/index.tsx';

const UNSCHEDULED_SIDEBAR_CONTENT_KEY = 'unscheduledSidebar';

export type UnscheduledSidebarProps = {
	onCloseSidebar: (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
	onClickIssue: (issueKey: string) => void;
};

export const useUnscheduledSidebar = ({
	UnscheduledSidebar,
	cleanSelectedIssueKey,
}: {
	UnscheduledSidebar: React.FC<UnscheduledSidebarProps>;
	cleanSelectedIssueKey: (x: string | undefined) => void;
}) => {
	const [selectedIssueKey, setSelectedIssueKey] = useQueryParam(SELECTED_ISSUE_PARAM);
	const [showIssueFromUnscheduledSidebar, setShowIssueFromUnscheduledSidebar] = useState(false);
	const [{ content }, { setSidebarContent }] = useSidebar();
	const [isUnscheduledItemsSidebar, setIsUnscheduledItemsSidebar] = useState(false);

	const isUnscheduledSidebarOpen = useMemo(
		() => content?.key === UNSCHEDULED_SIDEBAR_CONTENT_KEY,
		[content],
	);

	const onClickIssue = useCallback(
		(issueKey: string) => {
			setShowIssueFromUnscheduledSidebar(true);
			setSelectedIssueKey(issueKey);
		},
		[setSelectedIssueKey],
	);

	const onCloseUnscheduledSidebar = useCallback(
		(e: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'button clicked', 'unscheduledSidebarButton', {
				open: false,
			});
			setIsUnscheduledItemsSidebar(false);
			setSidebarContent(null);
		},
		[setSidebarContent],
	);

	const onClickUnscheduledButton = useCallback(
		(e: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			if (isUnscheduledItemsSidebar) {
				onCloseUnscheduledSidebar(e, analyticsEvent);
			} else {
				fireUIAnalytics(analyticsEvent, 'button clicked', 'unscheduledSidebarButton', {
					open: true,
				});
				setIsUnscheduledItemsSidebar(true);
				cleanSelectedIssueKey(undefined);
			}
		},
		[cleanSelectedIssueKey, onCloseUnscheduledSidebar, isUnscheduledItemsSidebar],
	);

	useEffect(() => {
		if (!content || (content && content?.key !== UNSCHEDULED_SIDEBAR_CONTENT_KEY)) {
			setIsUnscheduledItemsSidebar(false);
		}
	}, [content]);

	useEffect(() => {
		if (isUnscheduledItemsSidebar) {
			const unescheduledContent = (
				<ApolloClientProvider key={UNSCHEDULED_SIDEBAR_CONTENT_KEY} client={client}>
					<UnscheduledSidebar
						onCloseSidebar={onCloseUnscheduledSidebar}
						onClickIssue={onClickIssue}
					/>
				</ApolloClientProvider>
			);

			setSidebarContent(unescheduledContent);
		}
	}, [
		setSidebarContent,
		UnscheduledSidebar,
		onCloseUnscheduledSidebar,
		isUnscheduledItemsSidebar,
		onClickIssue,
	]);

	useEffect(() => {
		if (!selectedIssueKey) {
			setShowIssueFromUnscheduledSidebar(false);
		}
	}, [selectedIssueKey]);

	return {
		showIssueFromUnscheduledSidebar,
		isUnscheduledSidebarOpen,
		onCloseUnscheduledSidebar,
		onClickUnscheduledButton,
	};
};

export const useUnscheduledItems = () => {
	const [searchText, setSearchText] = useState<string>('');
	const { jql: jqlSimpleSearch, highlight } = useSearchText(searchText);
	const [filters, setFilters] = useState<FiltersType>({});
	const [sortDirection, setSortDirection] = useState<SortingDirection>('ASC');

	const params = useMemo(
		() => ({
			sortDirection,
			jqlSimpleSearch,
			jqlFilters: serialiseJql(filters) ?? '',
		}),
		[sortDirection, jqlSimpleSearch, filters],
	);

	const { data, error, loading, loadNextPage, isLoadingNextPage, hasNextPage, refetch } =
		useUnscheduledItemsService(params);

	const refresh = useCallback(() => {
		refetch();
	}, [refetch]);

	// Refresh issues on real time events

	useIssueEvents(refresh);

	const { onScroll } = useScrollToEnd({
		onScrollToEnd: loadNextPage,
		isDisabled: isLoadingNextPage || !hasNextPage,
		options: {
			onEndReachedThreshold: 150,
		},
	});

	const toggleSortDirection = useCallback(
		(e: React.MouseEvent<HTMLElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'button clicked', 'setSortDirectionUnscheduledItems');
			setSortDirection((currentState) => {
				if (currentState === 'ASC') {
					return 'DESC';
				}
				return 'ASC';
			});
		},
		[],
	);

	const onClearFilters = useCallback(() => {
		setSearchText('');
		setFilters({});
	}, []);

	return {
		issues: data,
		loading,
		onScroll,
		sortDirection,
		toggleSortDirection,
		searchText,
		setSearchText,
		highlight,
		filters,
		setFilters,
		onClearFilters,
		isLoadingNextPage,
		error,
	};
};
